import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { Permission } from './types/permissions.ts';
import './print.css';
import Navbar from './components/Navbar.tsx';
import AuthPage from './components/AuthPage.tsx';
import Dashboard from './components/Dashboard.tsx';
import UserManagement from './components/UserManagement.tsx';
import EmployeeManagement from './components/EmployeeManagement.tsx';
import TimesheetEntry from './components/TimesheetEntry.tsx';
import BulkTimesheetEntry from './components/BulkTimesheetEntry.tsx';
import TimesheetProcessor from './components/TimesheetProcessor.tsx';
import TimesheetManagement from './components/TimesheetManagement.tsx';
import InvoiceManagement from './components/InvoiceManagement.tsx';
import ContractorManagement from './components/ContractorManagement.tsx';
import ExcelInvoiceGenerator from './components/ExcelInvoiceGenerator.tsx';
import InvoiceUpload from './components/InvoiceUpload.tsx';
import InvoiceView from './components/InvoiceView.tsx';
import InvoiceEdit from './components/InvoiceEdit.tsx';
import ExecutiveDashboard from './components/ExecutiveDashboard.tsx';
import GustoCSVImport from './components/GustoCSVImport.tsx';
import TimesheetToInvoiceConverter from './components/TimesheetToInvoiceConverter.tsx';
import BatchTimesheetToInvoiceConverter from './components/BatchTimesheetToInvoiceConverter.tsx';
import TimesheetImport from './components/TimesheetImport.tsx';
import TimesheetReports from './components/TimesheetReports.tsx';
import EmployeeTimeReview from './components/EmployeeTimeReview.tsx';
import EmployeePerformanceAnalytics from './components/EmployeePerformanceAnalytics.tsx';
import PerformanceMethodology from './components/PerformanceMethodology.tsx';

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-brand-dark via-gray-900 to-brand-dark flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 bg-brand-gold rounded-2xl flex items-center justify-center mb-4 mx-auto animate-pulse">
        <span className="text-brand-dark font-black text-xl">7</span>
      </div>
      <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
      <p className="text-gray-400 mt-4">Loading...</p>
    </div>
  </div>
);

// Protected Route component with permission-based access control
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requireAll?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredPermission, 
  requiredPermissions = [], 
  requireAll = false 
}) => {
  const { currentUser, loading, hasPermission, hasAnyPermission } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Check permissions
  let hasAccess = false;

  if (requiredPermission) {
    hasAccess = hasPermission(requiredPermission);
  } else if (requiredPermissions.length > 0) {
    if (requireAll) {
      hasAccess = requiredPermissions.every(permission => hasPermission(permission));
    } else {
      hasAccess = hasAnyPermission(requiredPermissions);
    }
  } else {
    hasAccess = true; // No permissions specified
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-dark hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-gold"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Main App Layout
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main>{children}</main>
    </div>
  );
};

// Placeholder components for future development

const TimesheetsPage: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Timesheet Management</h1>
      <p className="text-gray-600">This feature will be implemented in Phase 2 - Week 4</p>
    </div>
  </div>
);

const InvoicesPage: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Invoice Management</h1>
      <p className="text-gray-600">This feature will be implemented in Phase 2 - Week 5</p>
    </div>
  </div>
);

const ContractorsPage: React.FC = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Contractor Management</h1>
      <p className="text-gray-600">This feature will be implemented in Phase 2 - Week 6</p>
    </div>
  </div>
);

// Auth wrapper component
const AuthWrapper: React.FC = () => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!currentUser) {
    return <AuthPage />;
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        
        {/* User Management - Admin only */}
        <Route 
          path="/users" 
          element={
            <ProtectedRoute requiredPermission={Permission.MANAGE_USERS}>
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Employee Management - Requires view employees permission */}
        <Route 
          path="/employees" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_EMPLOYEES}>
              <EmployeeManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Employee Performance Analytics - Requires view employee analytics permission */}
        <Route 
          path="/employees/performance" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_EMPLOYEE_ANALYTICS}>
              <EmployeePerformanceAnalytics />
            </ProtectedRoute>
          } 
        />
        
        {/* Performance Methodology - Requires view employee analytics permission */}
        <Route 
          path="/employees/performance/methodology" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_EMPLOYEE_ANALYTICS}>
              <PerformanceMethodology />
            </ProtectedRoute>
          } 
        />
        
        {/* Gusto CSV Import - Requires import employees permission */}
        <Route 
          path="/employees/gusto-csv-import" 
          element={
            <ProtectedRoute requiredPermission={Permission.IMPORT_EMPLOYEES}>
              <GustoCSVImport />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Management - Requires view timesheets permission */}
        <Route 
          path="/timesheets" 
          element={
            <ProtectedRoute requiredPermissions={[Permission.VIEW_TIMESHEETS, Permission.VIEW_OWN_TIMESHEETS]}>
              <TimesheetManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Entry - Requires create timesheets or submit timesheets permission */}
        <Route 
          path="/timesheets/entry" 
          element={
            <ProtectedRoute requiredPermissions={[Permission.CREATE_TIMESHEETS, Permission.SUBMIT_TIMESHEETS]}>
              <TimesheetEntry />
            </ProtectedRoute>
          } 
        />
        
        {/* Bulk Timesheet Entry - Requires bulk timesheet entry permission */}
        <Route 
          path="/timesheets/bulk" 
          element={
            <ProtectedRoute requiredPermission={Permission.BULK_TIMESHEET_ENTRY}>
              <BulkTimesheetEntry />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Processor - Requires process timesheets permission */}
        <Route 
          path="/timesheets/processor" 
          element={
            <ProtectedRoute requiredPermission={Permission.PROCESS_TIMESHEETS}>
              <TimesheetProcessor />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Entry with ID - Requires edit or submit timesheets permission */}
        <Route 
          path="/timesheets/entry/:id" 
          element={
            <ProtectedRoute requiredPermissions={[Permission.EDIT_TIMESHEETS, Permission.SUBMIT_TIMESHEETS]}>
              <TimesheetEntry />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Import - Requires import timesheets permission */}
        <Route 
          path="/timesheets/import" 
          element={
            <ProtectedRoute requiredPermission={Permission.IMPORT_TIMESHEETS}>
              <TimesheetImport />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet Reports - Requires view timesheet reports permission */}
        <Route 
          path="/timesheets/reports" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_TIMESHEET_REPORTS}>
              <TimesheetReports />
            </ProtectedRoute>
          } 
        />
        
        {/* Employee Time Review - Requires review employee time permission */}
        <Route 
          path="/timesheets/employee-review" 
          element={
            <ProtectedRoute requiredPermission={Permission.REVIEW_EMPLOYEE_TIME}>
              <EmployeeTimeReview />
            </ProtectedRoute>
          } 
        />
        
        {/* Timesheet to Invoice Converter - Requires convert timesheets to invoices permission */}
        <Route 
          path="/timesheets/convert-to-invoice/:id" 
          element={
            <ProtectedRoute requiredPermission={Permission.CONVERT_TIMESHEETS_TO_INVOICES}>
              <TimesheetToInvoiceConverter />
            </ProtectedRoute>
          } 
        />
        
        {/* Batch Timesheet to Invoice Converter - Requires batch invoice conversion permission */}
        <Route 
          path="/timesheets/batch-convert" 
          element={
            <ProtectedRoute requiredPermission={Permission.BATCH_INVOICE_CONVERSION}>
              <BatchTimesheetToInvoiceConverter />
            </ProtectedRoute>
          } 
        />
        
        {/* Invoice Management - Requires view invoices or view own invoices permission */}
        <Route 
          path="/invoices" 
          element={
            <ProtectedRoute requiredPermissions={[Permission.VIEW_INVOICES, Permission.VIEW_OWN_INVOICES]}>
              <InvoiceManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Excel Invoice Generator - Requires generate excel invoices permission */}
        <Route 
          path="/invoices/excel-generator" 
          element={
            <ProtectedRoute requiredPermission={Permission.GENERATE_EXCEL_INVOICES}>
              <ExcelInvoiceGenerator />
            </ProtectedRoute>
          } 
        />
        
        {/* Invoice Upload - Requires create invoices permission */}
        <Route 
          path="/invoices/upload" 
          element={
            <ProtectedRoute requiredPermission={Permission.CREATE_INVOICES}>
              <InvoiceUpload />
            </ProtectedRoute>
          } 
        />
        
        {/* Individual Invoice View - Requires view invoices or view own invoices permission */}
        <Route 
          path="/invoices/:id" 
          element={
            <ProtectedRoute requiredPermissions={[Permission.VIEW_INVOICES, Permission.VIEW_OWN_INVOICES]}>
              <InvoiceView />
            </ProtectedRoute>
          } 
        />
        
        {/* Individual Invoice Edit - Requires edit invoices permission */}
        <Route 
          path="/invoices/:id/edit" 
          element={
            <ProtectedRoute requiredPermission={Permission.EDIT_INVOICES}>
              <InvoiceEdit />
            </ProtectedRoute>
          } 
        />
        
        {/* Contractor Management - Requires view contractors permission */}
        <Route 
          path="/contractors" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_CONTRACTORS}>
              <ContractorManagement />
            </ProtectedRoute>
          } 
        />
        
        {/* Executive Dashboard - Requires view analytics permission */}
        <Route 
          path="/analytics" 
          element={
            <ProtectedRoute requiredPermission={Permission.VIEW_ANALYTICS}>
              <ExecutiveDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Catch all route - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <Routes>
            <Route path="/login" element={<AuthPage />} />
            <Route path="/*" element={<AuthWrapper />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
